import { Alert, AutoComplete, Button, Checkbox, Col, DatePicker, Form, Input, Modal, Row, Select, Space, UploadFile } from "antd";
import { RuleObject } from "antd/lib/form";
import { CardCategories, CardPhotoType, MaxTitleLength } from "models/cardIngestion";
import { StaffPackage } from "models/packages";
import { ListingType } from "models/pendingListings";
import { DisplayDateFormat, DisplayTimeFormat } from "models/websiteSettings";
import moment from "moment";
import { useCardTitleContext } from "pages/typer/CardTitleContext";
import { useCallback, useEffect, useState } from "react";
import { dollarsToCents, toSelectOptions } from "util/helpers";
import PackageSelector from "../../PackageSelector";
import UploadCardImages, { CardImageUploadResult } from "./UploadCardImages";
import { useCardImageUpload } from "./useCardRelistHooks";
import useNewListing, { ListingForm, ListingResult } from "./useNewListing";
import usePotentialSku from "./usePotentialSku";

const ListingResultAlert = ({ success, sku, errors }: ListingResult) => {
    const props = success
        ? {
            type: "success" as any,
            message: "Listing Created",
            description: `Assigned SKU: ${sku.value}`
        }
        : {
            type: "error" as any,
            message: "Listing Failed",
            description: <>
                {errors.map(e => (<p>{e.code} - {e.message}</p>))}
            </>
        };

    return <Alert
        showIcon
        style={{ marginBottom: '5px' }}
        {...props}
    />;
}

const getStartingPriceLabel = (type: ListingType) => {
    if (type === ListingType.BuyItNow || type === ListingType.BestOffer) {
        return "Buy It Now Price";
    }

    return "Starting Price";
}

const NewListingModal = () => {
    const [open, setOpen] = useState<boolean>(false);
    const [form] = Form.useForm<ListingForm>();
    const [images, setImages] = useState<CardImageUploadResult[]>([]);
    const [imageKeys, setImageKeys] = useState([crypto.randomUUID(), crypto.randomUUID(), crypto.randomUUID()]);
    const [tempSku, setTempSku] = useState<string | null>(null);
    const [isCleaningUp, setIsCleaningUp] = useState(false);
    const { mutateAsync: getPotentialSku } = usePotentialSku();
    const { createAdHocListing, isLoading } = useNewListing();
    const { removeImage } = useCardImageUpload();
    const [listingResult, setListingResult] = useState<ListingResult | undefined>();

    const { package: pkg, type } = Form.useWatch([], form) ?? {};

    const {
        id,
        cardTitle,
        isLot,
        category,
        subCategory,
        statusId,
        conditionId,
        auctionCategory,
        gradingCompany,
        grader,
        grade,
        isGraded,
        working,
        availableSubCategories,
        createNewCard,
        reset,
        setTitle,
        setIsLot,
        setCategory,
        setSubcategory,
        setStatus,
        setGradingCompany,
        setCondition,
        setUseNavigation,
    } = useCardTitleContext();

    useEffect(() => {
        if (open) {
            setImageKeys([
                crypto.randomUUID(),
                crypto.randomUUID(),
                crypto.randomUUID()
            ]);
            setUseNavigation(false);
            createNewCard();
        }
    }, [open, createNewCard, setUseNavigation]);

    useEffect(() => {
        if (open) {
            form.setFieldsValue({
                cardTitle,
                isLot,
                category,
                subCategory,
                statusId,
                conditionId,
                grader,
                grade
            });
        }
    }, [
        form,
        open,
        cardTitle,
        isLot,
        category,
        subCategory,
        statusId,
        conditionId,
        grader,
        grade
    ]);

    const updateSku = useCallback(async (pkg: StaffPackage | undefined, isLot: boolean, isGraded: boolean) => {
        if (pkg?.id) {
            const result = await getPotentialSku({ packageId: pkg.id, isLot, isGraded });
            setTempSku(result.sku);
        } else {
            setTempSku(null);
        }
    }, [setTempSku, getPotentialSku]);

    useEffect(() => {
        updateSku(pkg, isLot, isGraded);
    }, [pkg, isLot, isGraded, updateSku]);

    const imageAdded = useCallback((result: CardImageUploadResult) => {
        setImages((cur) => [...cur, result]);
    }, []);

    const imagePreviewed = useCallback((file: UploadFile) => {
        const img = images.find(f => f.uid === file.uid);
        if (img) {
            window.open(img.url, "_blank");
        }
    }, [images]);

    const imageRemoved = useCallback((uid: string) => {
        setImages((cur) => [...cur.filter(x => x.uid !== uid)]);
    }, []);

    const assignDetachedFields = () => {
        const isBestOffer = type === ListingType.BestOffer;

        const {
            minOfferPrice,
            minOfferInCents,
            autoAcceptPrice,
            autoAcceptInCents
        } = form.getFieldsValue(['minOfferPrice', 'minOfferInCents', 'autoAcceptPrice', 'autoAcceptInCents']);

        form.setFieldsValue({
            frontImage: images.find(x => x.type === CardPhotoType.Front),
            backImage: images.find(x => x.type === CardPhotoType.Back),
            extraImages: images.filter(x => x.type === CardPhotoType.Extra),

            // setting an empty string will force validation errors. nulls won't do that 
            // for some reason, regardless of if I allow them or not on the ListingForm interface
            minOfferPrice: isBestOffer ? minOfferPrice ?? '' : null,
            minOfferInCents: isBestOffer ? minOfferInCents ?? '' : null,
            autoAcceptPrice: isBestOffer ? autoAcceptPrice ?? '' : null,
            autoAcceptInCents: isBestOffer ? autoAcceptInCents ?? '' : null,
        });
    }

    const close = async () => {
        setOpen(false);
        setListingResult(undefined);
        form.resetFields();
        await reset();
    }

    const submit = async () => {
        if (listingResult?.success) {
            await close();
            setImages([]);
            return;
        }

        setListingResult(undefined);

        assignDetachedFields();
        const validForm = await form.validateFields();
        const result = await createAdHocListing(validForm);

        setListingResult(result);
    }

    const cancel = async () => {
        setIsCleaningUp(true);

        await removeImages();
        await close();

        setIsCleaningUp(false);
    }

    const removeImages = async () => {
        for (const img of images) {
            await removeImage(img.url!);
        }
        setImages([]);
    }

    const amountValidator = (_: RuleObject, price: number, inCentsKey: string) => {
        if (isNaN(price)) {
            return Promise.reject(new Error("Not a valid amount!"));
        } else if (price <= 0) {
            return Promise.reject(
                new Error("Amount must be greater than zero!")
            );
        }
        form.setFieldsValue({ [inCentsKey]: price });
        return Promise.resolve();
    };

    return <>
        <Button type="primary" onClick={() => setOpen(true)}>Create New Listing</Button>
        <Modal
            title="Create New Listing"
            onOk={submit}
            onCancel={cancel}
            cancelButtonProps={{ loading: isLoading || isCleaningUp, disabled: listingResult !== undefined && listingResult.success }}
            open={open}
            width='850px'
            keyboard={false}
            maskClosable={false}
            closable={false}
            confirmLoading={isLoading || isCleaningUp}
        >
            <Form
                form={form}
                preserve={false}
                layout="vertical"
                disabled={working || isLoading || isCleaningUp || listingResult?.success}
                initialValues={{
                    type: ListingType.Standard,
                    scheduledTime: moment().set({ 'hour': 21, minute: 0, second: 0 }),
                    startingPrice: '0.99',
                    startingPriceInCents: 99
                }}
            >
                <Row gutter={[32, 0]}>
                    <Col span={24}>
                        {listingResult && <ListingResultAlert {...listingResult} />}
                    </Col>
                    <Col span={18}>
                        <Form.Item
                            label="Card Title"
                            name="cardTitle"
                            rules={[
                                { required: true, message: 'Card title is required' },
                                { max: MaxTitleLength, message: `Card title has to be ${MaxTitleLength} characters or less` }
                            ]}>
                            <Input
                                showCount={{ formatter: (args) => `${args.count} / ${MaxTitleLength}` }}
                                onChange={e => setTitle(e.target.value)}
                                spellCheck
                            />
                        </Form.Item>
                    </Col>

                    <Col span={6}>
                        <Form.Item
                            label="Package ID"
                            name="package"
                            rules={[{ required: true, message: "Package ID is required!" }]}
                            help={<div style={{ display: 'flex', justifySelf: 'end', position: 'absolute' }}>{tempSku}</div>}>
                            <PackageSelector
                                onSelected={(pkg) => form.setFieldsValue({ package: pkg })}
                                onClear={() => form.setFieldsValue({ package: undefined })}
                            />
                        </Form.Item>
                    </Col>

                    <Col span={6}>
                        <Form.Item
                            label="Category"
                            name="category"
                            rules={[
                                { required: true }
                            ]}
                        >
                            <Select options={CardCategories} onChange={setCategory} />
                        </Form.Item>
                    </Col>

                    <Col span={12}>
                        <Form.Item
                            label="Sub Category"
                            name="subCategory"
                            rules={[
                                { required: true, message: 'You have to enter a sub category' }
                            ]}
                        >
                            <AutoComplete
                                filterOption
                                defaultOpen={false}
                                onChange={setSubcategory}
                                options={availableSubCategories.map(value => ({ value }))}
                            />
                        </Form.Item>
                    </Col>

                    <Col span={5}>
                        <Form.Item
                            label=" "
                            valuePropName="checked"
                            name="isLot"
                        >
                            <Checkbox onChange={e => setIsLot(e.target.checked)}>Lot Listing</Checkbox>
                        </Form.Item>
                    </Col>

                    <Col span={6}>
                        <Form.Item
                            label="Status"
                            name="statusId"
                            rules={[
                                { required: true }
                            ]}
                        >
                            <Select
                                options={toSelectOptions(auctionCategory.itemConditions)}
                                onChange={v => setStatus(v)}
                            />
                        </Form.Item>
                    </Col>

                    <Col span={12} hidden={!isGraded}>
                        <Form.Item
                            label="Grader"
                            name="grader"
                            rules={[
                                { required: isGraded, message: 'You have to select a grader' }
                            ]}
                        >
                            <Select
                                options={toSelectOptions(auctionCategory.graders, true)}
                                onChange={v => setGradingCompany(v)}
                            />
                        </Form.Item>
                    </Col>

                    <Col span={6} hidden={!isGraded}>
                        <Form.Item
                            label="Grade"
                            name="grade"
                            rules={[
                                { required: isGraded, message: 'You have to select a grade' }
                            ]}
                        >
                            <Select disabled={!gradingCompany}
                                options={toSelectOptions(gradingCompany?.grades, true)}
                                onChange={v => setGradingCompany(grader, v)}
                            />
                        </Form.Item>
                    </Col>

                    <Col span={8} hidden={isGraded || isLot}>
                        <Form.Item
                            label="Condition"
                            name="conditionId"
                            rules={[
                                { required: !isGraded && !isLot }
                            ]}
                        >
                            <Select
                                options={toSelectOptions(auctionCategory.ungradedConditions)}
                                onChange={setCondition}
                            />
                        </Form.Item>
                    </Col>

                    <Col span={24}>
                        <Space direction="horizontal">
                            <Form.Item label="Front Image" name="frontImage" rules={[{ required: true, message: "Missing front image" }]}>
                                <UploadCardImages
                                    key={imageKeys[0]}
                                    cardId={id}
                                    label={false}
                                    type={CardPhotoType.Front}
                                    onAdded={imageAdded}
                                    onPreview={imagePreviewed}
                                    onRemove={imageRemoved}
                                />
                            </Form.Item>

                            <Form.Item label="Back Image" name="backImage" rules={[{ required: true, message: "Missing back image" }]}>
                                <UploadCardImages
                                    key={imageKeys[1]}
                                    cardId={id}
                                    label={false}
                                    type={CardPhotoType.Back}
                                    onAdded={imageAdded}
                                    onPreview={imagePreviewed}
                                    onRemove={imageRemoved}
                                />
                            </Form.Item>

                            <Form.Item label="Extra Images" name="extraImages" rules={[{ required: false }]}>
                                <UploadCardImages
                                    key={imageKeys[2]}
                                    cardId={id}
                                    label={false}
                                    type={CardPhotoType.Extra}
                                    onAdded={imageAdded}
                                    onPreview={imagePreviewed}
                                    onRemove={imageRemoved}
                                />
                            </Form.Item>
                        </Space>
                    </Col>

                    <Col span={5}>
                        <Form.Item label="Listing Type" name="type">
                            <Select
                                options={[
                                    { label: 'Standard', value: ListingType.Standard },
                                    { label: 'Buy It Now', value: ListingType.BuyItNow },
                                    { label: 'Best Offer', value: ListingType.BestOffer },
                                ]}
                            />
                        </Form.Item>
                    </Col>

                    <Col span={6}>
                        <Form.Item
                            label={getStartingPriceLabel(type)}
                            name="startingPrice"
                            rules={[{
                                required: true,
                                validator: (r, value) => amountValidator(r, value, 'startingPriceInCents'),
                                transform: dollarsToCents
                            }]}>
                            <Input prefix="$" suffix="USD" />
                        </Form.Item>
                        <Form.Item hidden name="startingPriceInCents">
                            <Input type="hidden" />
                        </Form.Item>
                    </Col>

                    <Col span={6}>
                        <Form.Item
                            label="Minimum Offer"
                            name="minOfferPrice"
                            hidden={type !== ListingType.BestOffer}
                            rules={[{
                                required: type === ListingType.BestOffer,
                                validator: (r, value) => amountValidator(r, value, 'minOfferInCents'),
                                transform: dollarsToCents
                            }]}>
                            <Input prefix="$" suffix="USD" />
                        </Form.Item>
                        <Form.Item hidden name="minOfferInCents">
                            <Input type="hidden" />
                        </Form.Item>
                    </Col>

                    <Col span={6}>
                        <Form.Item
                            label="Auto-Accept Offer"
                            name="autoAcceptPrice"
                            hidden={type !== ListingType.BestOffer}
                            rules={[{
                                required: type === ListingType.BestOffer,
                                validator: (r, value) => amountValidator(r, value, 'autoAcceptInCents'),
                                transform: dollarsToCents
                            }]}>
                            <Input prefix="$" suffix="USD" />
                        </Form.Item>
                        <Form.Item hidden name="autoAcceptInCents">
                            <Input type="hidden" />
                        </Form.Item>
                    </Col>

                    <Col span={7}>
                        <Form.Item
                            label="Scheduled Time (Eastern)"
                            name="scheduledTime"
                            hidden={type === ListingType.BuyItNow}
                            rules={[{ required: type !== ListingType.BuyItNow, message: "Scheduled time is required!" }]}>
                            <DatePicker
                                format={`${DisplayDateFormat}  ${DisplayTimeFormat}`}
                                showTime={{ defaultValue: moment('21:00:00', 'HH:mm:ss') }}
                                disabledDate={(date: moment.Moment) =>
                                    date.isBefore(moment().subtract(1, 'days')) || date.isAfter(moment().add(3, 'weeks'))}
                            />
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
        </Modal>
    </>
};

export default NewListingModal;